import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { nanoid } from 'nanoid'
import classNames from 'classnames'

import { Tag } from './add-multi-values-tags'
import { ButtonRow } from './button-row'
import Button from './button'
import { DateSelector } from './campaign-code-generator-rows'
import HeaderPanel, { Panel } from './header-panel'
import { LoadingLabel } from './loader'
import { SelectBoxChecklist } from './select-box'
import { DimensionOption, SelectedDimensions } from '../api/types'
import ClearIcon from '../assets/icon-close-grey.svg'
import { AvailableDimension } from '../context/reducers/graphql-reducer'
import useLogAction from '../hooks/useLogAction'
import { saveUserData } from '../local-client/local-client'
import styles from '../styles/collapsable-filters.module.scss'

interface DimensionSelectorProps {
  className?: string
  item: AvailableDimension
  onChange: (option: DimensionOption) => void
  defaultValue: DimensionOption
  headlineFilter?: boolean
  paramsRequired?: {
    fieldID: string
    required: boolean
  }[]
  /** Used when SelcetBoxChecklist is in the top-line data summary. It affects the presence of 'All' label */
  greyChecklist?: boolean
}

export function DimensionSelector({
  className,
  item,
  onChange,
  defaultValue,
  headlineFilter = false,
  paramsRequired,
  greyChecklist,
}: DimensionSelectorProps): React.ReactElement {
  const {
    dimensionName,
    dimensionParameterID,
    dimensionOptions,
    parameterType,
    dateFormat,
  } = item

  const [value, setValue] = useState<DimensionOption>(
    JSON.parse(JSON.stringify(defaultValue)),
  )

  useEffect(() => {
    setValue(JSON.parse(JSON.stringify(defaultValue)))
  }, [defaultValue])

  if (parameterType === 'date' && !!dateFormat) {
    return (
      <div className={classNames(styles.formRow, styles.wrapper)}>
        <DateSelector
          inlineLabel={dimensionName}
          fullWidth
          dateFormat={dateFormat}
          value={
            value.dimensionOptions.length > 0 ? value.dimensionOptions[0] : ''
          }
          onChange={(val) => {
            const useValue =
              val === ''
                ? { ...value, dimensionOptions: [] }
                : { ...value, dimensionOptions: [val] }

            setValue(useValue)
            onChange(useValue)
          }}
        />
      </div>
    )
  }

  const fullFilterList = dimensionOptions
    ? dimensionOptions.map((i) => {
        return { optionName: i === '' ? '(empty)' : i, optionValue: i }
      })
    : []

  if (
    parameterType === 'select' &&
    paramsRequired &&
    !paramsRequired.find((param) => param.fieldID === dimensionParameterID)
      ?.required
  ) {
    fullFilterList.unshift({ optionName: '(not set)', optionValue: '' })
  }

  return (
    <div className={classNames(className, styles.formRow, styles.wrapper)}>
      <SelectBoxChecklist
        id={dimensionName}
        className={styles.select}
        variant={greyChecklist ? 'grey' : 'white'}
        excludeAnyNone
        allLabel={greyChecklist ? '' : 'All'}
        controlLabel={
          headlineFilter &&
          dimensionOptions &&
          (value.dimensionOptions.length === 0 ||
            value.dimensionOptions.length === dimensionOptions.length)
            ? `all ${dimensionName}s`
            : dimensionName
        }
        placeholder={
          headlineFilter &&
          dimensionOptions &&
          (value.dimensionOptions.length === 0 ||
            value.dimensionOptions.length === dimensionOptions.length)
            ? `all ${dimensionName}s`
            : dimensionName
        }
        showOnlyButtons
        isSearchable
        labelKey="optionName"
        valueKey="optionValue"
        value={fullFilterList.filter(
          (option) => value.dimensionOptions.indexOf(option.optionValue) > -1,
        )}
        options={fullFilterList}
        onChange={(newValue) => {
          const newDimensionOptions = newValue.map(
            ({ optionValue }) => optionValue,
          )

          setValue({ ...value, dimensionOptions: newDimensionOptions })
          onChange({ ...value, dimensionOptions: newDimensionOptions })
        }}
      />
    </div>
  )
}

export interface LinkFilter {
  id: string
  link: string
}

interface CollapsableFiltersSummaryProps {
  data: any[]
  selectedDimensions: SelectedDimensions
  /** Used for one-click reports from Track>View. Users should se which URL is selected */
  linkFilter?: LinkFilter | null
  setLinkFilter?: React.Dispatch<React.SetStateAction<LinkFilter | null>>
  onChange: (val: DimensionOption, item: any) => void
}

export function CollapsableFiltersSummary({
  data,
  onChange,
  linkFilter,
  setLinkFilter,
  selectedDimensions,
}: CollapsableFiltersSummaryProps): React.ReactElement | null {
  const filtered = data.filter((dimension) => {
    const { dimensionName, dimensionOptions } = dimension

    const hasValues = Object.prototype.hasOwnProperty.call(
      selectedDimensions,
      dimensionName,
    )

    const allSelected =
      hasValues &&
      selectedDimensions[dimensionName].dimensionOptions.length ===
        dimensionOptions.length

    const noneSelected =
      hasValues &&
      selectedDimensions[dimensionName].dimensionOptions.length === 0

    const doNotShow = (hasValues && allSelected) || noneSelected || !hasValues

    return !doNotShow
  })

  if (filtered.length === 0 && !linkFilter) {
    return null
  }

  return (
    <HeaderPanel className={styles.headerPanel}>
      <Panel>
        <ButtonRow
          className={classNames(styles.buttonRow, styles.buttonRowTags)}
        >
          <p className={styles.summaryTitle}>
            <strong>Filtered on:</strong>
          </p>
          {linkFilter && setLinkFilter && (
            <Tag
              key={nanoid()}
              className={classNames(styles.tag, styles.linkId)}
              compact
              onClick={() => setLinkFilter(null)}
            >
              <span className={styles.tagTitle}>Link:</span> {linkFilter.link}
            </Tag>
          )}
          {data.map((dimension) => {
            const { dimensionName, dimensionOptions } = dimension

            const hasValues = Object.prototype.hasOwnProperty.call(
              selectedDimensions,
              dimensionName,
            )
            const allSelected =
              hasValues &&
              selectedDimensions[dimensionName].dimensionOptions.length ===
                dimensionOptions.length
            const noneSelected =
              hasValues &&
              selectedDimensions[dimensionName].dimensionOptions.length === 0

            if ((hasValues && allSelected) || noneSelected || !hasValues) {
              return null
            }

            if (!(hasValues && !allSelected)) {
              return null
            }

            return selectedDimensions[dimensionName].dimensionOptions.map(
              (item) => {
                return (
                  <Tag
                    key={nanoid()}
                    className={styles.tag}
                    compact
                    onClick={() => {
                      const currentSelection =
                        selectedDimensions[dimensionName].dimensionOptions

                      const newSelection = currentSelection.filter(
                        (i) => i !== item,
                      )

                      onChange(
                        {
                          ...selectedDimensions[dimensionName],
                          dimensionOptions: [...newSelection],
                        },
                        dimension,
                      )
                    }}
                  >
                    <span className={styles.tagTitle}>{dimensionName}:</span>{' '}
                    {item === '' ? '(empty)' : item}
                  </Tag>
                )
              },
            )
          })}
        </ButtonRow>
      </Panel>
    </HeaderPanel>
  )
}
interface SupportMetricDropDownProps {
  successMetricsList: any[]
  allAvailableMetrics: any[]
  setSuccessMetricsList: any
  accountID?: string
}

export function SupportMetricDropDown({
  successMetricsList,
  allAvailableMetrics,
  setSuccessMetricsList,
  accountID = '',
}: SupportMetricDropDownProps): React.ReactElement {
  const useAllAvailableMetrics = useMemo(() => {
    return allAvailableMetrics.filter((item) => item.setupComplete)
  }, [allAvailableMetrics])

  return (
    <SelectBoxChecklist
      id="supportMetrics"
      className={classNames(styles.successMetricSelector, styles.reportTable)}
      isClearable={false}
      showOnlyButtons
      allLabel="All"
      controlLabel="Support metrics"
      placeholder="Support metrics: None"
      labelKey="displayName"
      valueKey="metricID"
      isDisabled={useAllAvailableMetrics.length === 0}
      value={useAllAvailableMetrics.filter(
        (option) => successMetricsList.indexOf(option.metricID) > -1,
      )}
      options={useAllAvailableMetrics}
      onChange={(newValue) => {
        const newList = newValue.map(({ metricID }) => metricID)

        setSuccessMetricsList(newList)

        if (accountID) {
          saveUserData({ successMetricsList: newList }, accountID)
        }
      }}
    />
  )
}

interface CollapsableFiltersProps {
  data: AvailableDimension[]
  loading?: boolean
  error?: boolean
  selectedDimensions: SelectedDimensions
  onChange: (val: DimensionOption, item: AvailableDimension) => void
  children?: React.ReactNode
  onResetFilters: () => void
  isMobile: boolean
  accountID?: string
  paramsRequired?: {
    fieldID: string
    required: boolean
  }[]
}

export default function CollapsableFilters({
  data,
  loading,
  error,
  onChange,
  onResetFilters,
  selectedDimensions,
  children,
  isMobile,
  paramsRequired,
}: CollapsableFiltersProps): React.ReactElement {
  const logAction = useLogAction()

  const [collapsed, setCollapsed] = useState(true)

  const hasFilters = useMemo(() => {
    return Object.keys(selectedDimensions).length > 0
  }, [selectedDimensions])

  const filtersButtons = useCallback(() => {
    let label = hasFilters ? 'Edit filters' : 'Apply filters'

    if (!collapsed) label = 'Hide filters'

    return (
      <Button
        className={styles.collapseButton}
        isDisabled={error}
        variant="secondary"
        onPress={async () => {
          if (collapsed) {
            setCollapsed(false)

            await logAction({
              variables: {
                action: 'edit-filters',
                extra: '',
                websiteSection: 'report',
                functionName: 'collapseFilter',
                pagePath: window.location.pathname,
              },
            })
          } else {
            setCollapsed(true)
          }
        }}
      >
        {label}
      </Button>
    )
  }, [hasFilters, collapsed, error])

  if (collapsed) {
    return (
      <>
        <div className={classNames(styles.box)}>
          <div className={styles.topRowBox}>
            <div className={styles.topRow}>
              {children}
              {!isMobile && (
                <div className={styles.buttonInlineWrapper}>
                  {filtersButtons()}
                </div>
              )}
            </div>
          </div>
          {isMobile && (
            <HeaderPanel className={styles.headerPanel}>
              <Panel className={styles.panel}>
                <ButtonRow className={styles.buttonRow}>
                  <div className={styles.fullRow}>
                    <div className={styles.filterButtonsWrapper}>
                      {filtersButtons()}
                    </div>
                  </div>
                </ButtonRow>
              </Panel>
            </HeaderPanel>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      <div
        className={classNames(
          styles.box,
          styles.topRowWrapper,
          styles.expandedBox,
        )}
      >
        <div className={styles.topRowBox}>
          <div className={styles.topRow}>
            {children}
            {!isMobile && (
              <div className={styles.buttonInlineWrapper}>
                {filtersButtons()}
              </div>
            )}
          </div>
        </div>
        <HeaderPanel className={styles.headerPanel}>
          <Panel className={styles.panel}>
            <ButtonRow className={styles.buttonRow}>
              {isMobile && (
                <div className={styles.fullRow}>
                  <div className={styles.filterButtonsWrapper}>
                    {filtersButtons()}
                  </div>
                </div>
              )}
              {loading || data.length === 0 ? (
                <LoadingLabel
                  label="Loading filters"
                  className={styles.loader}
                  fixedWidth
                />
              ) : (
                data.map((item) => {
                  const hasValues = Object.prototype.hasOwnProperty.call(
                    selectedDimensions,
                    item.dimensionName,
                  )

                  return (
                    <DimensionSelector
                      greyChecklist
                      defaultValue={
                        !hasValues
                          ? {
                              dimensionParameterID: item.dimensionParameterID,
                              dimensionOptions: [],
                            }
                          : selectedDimensions[item.dimensionName]
                      }
                      item={item}
                      key={JSON.stringify(item)}
                      paramsRequired={paramsRequired}
                      onChange={(val: DimensionOption) => onChange(val, item)}
                    />
                  )
                })
              )}
              {!loading && data.length !== 0 && (
                <div className={styles.wrapper}>
                  <Button
                    className={styles.resetButton}
                    variant="text"
                    color="grey"
                    onPress={() => onResetFilters()}
                    icon={{
                      src: ClearIcon,
                      alt: 'Clear all',
                    }}
                  >
                    Reset filters
                  </Button>
                </div>
              )}
            </ButtonRow>
          </Panel>
        </HeaderPanel>
      </div>
    </>
  )
}
